import { type FragmentType } from '@packages/gql/generated/shopping';
import { type CategoryItemContentFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryItemContentFragmentFragmentDoc';
import { type CategoryItemTextFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryItemTextFragmentFragmentDoc';
import { type CategoryItemContentFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { unmask } from '@packages/gql/src/betterMasking';

/** Displays the name of the category */
const CategoryItemText = ({
  maskedCategory,
}: {
  maskedCategory: FragmentType<typeof CategoryItemTextFragmentFragmentDoc>;
}) => {
  const category = unmask<typeof CategoryItemTextFragmentFragmentDoc>(maskedCategory);

  return (
    <span className={category.isSelected ? 'font-bold' : 'hyphens-auto'}>{category.name}</span>
  );
};

/** Displays the "plus" or "minus" icon showing expanded or collapsed categories */
const ExpandCollapseIcon = ({ isExpanded }: { isExpanded: boolean }) => (
  <span className="text-size-headline-base absolute -ml-2.5" aria-hidden>
    {isExpanded ? '﹣' : '﹢'}
  </span>
);

/** Displays the "Arrow" icon showing expanded or collapsed root categories */
const ArrowIcon = ({ category }: { category: CategoryItemContentFragmentFragment }) => (
  <span
    aria-hidden
    className={`${category.isSelected ? 'icon-emp-arrow-medium-down' : 'icon-emp-arrow-medium-right'} text-color-grey-dark size-4`}
  />
);

/**
 * Displays Content of menu item.
 * Level 2 and above should display a plus or minus sign, if it has children.
 * - Plus sign when children are collapsed
 * - Minus sign when children are expanded
 * If there are no children no icon should be displayed
 */
export const ProductCategoryNavigationDesktopItemContent = ({
  maskedCategory,
  isExpanded,
}: {
  maskedCategory: FragmentType<typeof CategoryItemContentFragmentFragmentDoc>;
  isExpanded?: boolean;
}) => {
  const category = unmask<typeof CategoryItemContentFragmentFragmentDoc>(maskedCategory);

  return (
    <>
      <span className={`flex ${category.level > 2 ? 'pl-3' : 'pl-1'}`}>
        {category.level > 2 && category.hasChildren && (
          <ExpandCollapseIcon isExpanded={isExpanded ?? Boolean(category.isSelected)} />
        )}
        <CategoryItemText maskedCategory={category} />
      </span>
      {category.hasChildren && category.level === 2 && <ArrowIcon category={category} />}
    </>
  );
};
